/*
Chooses an accessible text color to place on top of a given background color.

It returns whichever of white / black keeps the text readable, preferring white
(the typical look on a brand-colored fill) and falling back to black when white
does not meet the WCAG AA minimum contrast ratio of 4.5:1. Because black and
white are the luminance extremes, at least one of them always clears 4.5:1 for
any background, so the result is guaranteed accessible.

Previously this used a perceived-brightness threshold that returned grey
(#707070), which failed contrast against many brand colors — see accessibility
findings SHO_101, SHO_102, SHO_104, SHO_106, SHO_109.
*/

export function getContrast(hex) {
  const bg = [hexToR(hex), hexToG(hex), hexToB(hex)];

  return contrastRatio(bg, [255, 255, 255]) >= 4.5 ? '#ffffff' : '#000000';
}

function hexToR(h) { return parseInt(cutHex(h).substring(0, 2), 16); }
function hexToG(h) { return parseInt(cutHex(h).substring(2, 4), 16); }
function hexToB(h) { return parseInt(cutHex(h).substring(4, 6), 16); }
function cutHex(h) { return (h.charAt(0) === '#') ? h.substring(1, 7) : h; }

// WCAG 2.x relative luminance of an [r, g, b] color (channels 0-255).
function relativeLuminance([r, g, b]) {
  const toLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

// WCAG contrast ratio between two [r, g, b] colors (ranges from 1:1 to 21:1).
function contrastRatio(rgb1, rgb2) {
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}
